// =====================
// Tag System Types
// =====================

import { z } from 'zod';
import { ModuleTypeEnum } from './module-type';

// =====================
// Enums
// =====================

export const TagStatusEnum = z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
export type TagStatus = z.infer<typeof TagStatusEnum>;

// =====================
// Base Types
// =====================

/**
 * TagDefinition represents a reusable tag that can be applied to any module
 * Tags are unified across the entire system and available for all modules
 */
export interface TagDefinition {
    id: string;
    organizationId: string;
    name: string;
    slug: string;
    description?: string;
    color?: string; // Hex color code
    isActive: boolean;
    isSystemTag: boolean;
    usageCount: number;
    settings: Record<string, unknown>;
    createdBy?: string;
    createdAt: Date;
    updatedAt: Date;
    deletedAt?: Date;
}

/**
 * TagAssignment represents a tag being applied to a specific module instance
 */
export interface TagAssignment {
    id: string;
    organizationId: string;
    tagId: string;
    moduleType: string; // ModuleType enum value
    moduleId: string;
    assignedBy?: string;
    assignedAt: Date;
    context?: string;
}

/**
 * TagWithAssignments includes the tag definition with its assignments
 */
export interface TagWithAssignments extends TagDefinition {
    assignments: TagAssignment[];
}

/**
 * ModuleWithTags represents any module entity with its assigned tags
 */
export interface ModuleWithTags {
    id: string;
    tags: TagDefinition[];
}

// =====================
// DTOs (Data Transfer Objects)
// =====================

export interface CreateTagDefinitionDto {
    name: string;
    description?: string;
    color?: string;
    settings?: Record<string, unknown>;
}

export interface UpdateTagDefinitionDto {
    name?: string;
    description?: string;
    color?: string;
    isActive?: boolean;
    settings?: Record<string, unknown>;
}

export interface CreateTagAssignmentDto {
    tagId: string;
    moduleType: string;
    moduleId: string;
    context?: string;
}

export interface BatchCreateTagAssignmentDto {
    tagIds: string[];
    moduleType: string;
    moduleId: string;
    context?: string;
}

export interface BatchDeleteTagAssignmentDto {
    tagIds: string[];
    moduleType: string;
    moduleId: string;
}

export interface BulkGetModuleTagsDto {
    moduleType: string;
    moduleIds: string[];
}

// =====================
// Query and Filter Types
// =====================

export interface TagFilters {
    search?: string;
    isActive?: boolean;
    isSystemTag?: boolean;
    createdBy?: string;
    hasAssignments?: boolean;
    usageCountMin?: number;
    usageCountMax?: number;
}

export interface TagAssignmentFilters {
    tagIds?: string[];
    moduleType?: string;
    moduleIds?: string[];
    assignedBy?: string;
    dateFrom?: Date;
    dateTo?: Date;
}

export const TagSortFieldEnum = z.enum([
    'name',
    'createdAt', 
    'updatedAt',
    'usageCount'
]);
export type TagSortField = z.infer<typeof TagSortFieldEnum>;

// =====================
// Response Types
// =====================

export interface TagDefinitionResponse {
    tag: TagDefinition;
    assignmentCount?: number;
}

export interface TagDefinitionsResponse {
    tags: TagDefinition[];
    total: number;
    page: number;
    pageSize: number;
}

export interface TagAssignmentResponse {
    assignment: TagAssignment;
    tag: TagDefinition;
}

export interface TagAssignmentsResponse {
    assignments: TagAssignment[];
    total: number;
    page: number;
    pageSize: number;
}

export interface ModuleTagsResponse {
    moduleType: string;
    moduleId: string;
    tags: TagDefinition[];
}

export interface TagUsageStats {
    tagId: string;
    tagName: string;
    usageCount: number;
    moduleBreakdown: Record<string, number>;
    recentUsage: Date[];
}

// =====================
// Zod Schemas for Validation
// =====================

export const TagDefinitionSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    isActive: z.boolean(),
    isSystemTag: z.boolean(),
    usageCount: z.number().min(0),
    settings: z.record(z.unknown()),
    createdBy: z.string().uuid().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
});

export const CreateTagDefinitionSchema = z.object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    settings: z.record(z.unknown()).optional(),
});

export const UpdateTagDefinitionSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    isActive: z.boolean().optional(),
    settings: z.record(z.unknown()).optional(),
});

export const TagAssignmentSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    tagId: z.string().uuid(),
    moduleType: z.string(),
    moduleId: z.string().uuid(),
    assignedBy: z.string().uuid().optional(),
    assignedAt: z.date(),
    context: z.string().max(255).optional(),
});

export const CreateTagAssignmentSchema = z.object({
    tagId: z.string().uuid(),
    moduleType: z.string(),
    moduleId: z.string().uuid(),
    context: z.string().max(255).optional(),
});

export const BatchCreateTagAssignmentSchema = z.object({
    tagIds: z.array(z.string().uuid()).min(1),
    moduleType: z.string(),
    moduleId: z.string().uuid(),
    context: z.string().max(255).optional(),
});

export const BatchDeleteTagAssignmentSchema = z.object({
    tagIds: z.array(z.string().uuid()).min(1),
    moduleType: z.string(),
    moduleId: z.string().uuid(),
});

export const TagFiltersSchema = z.object({
    search: z.string().optional(),
    isActive: z.boolean().optional(),
    isSystemTag: z.boolean().optional(),
    createdBy: z.string().uuid().optional(),
    hasAssignments: z.boolean().optional(),
    usageCountMin: z.number().min(0).optional(),
    usageCountMax: z.number().min(0).optional(),
});

// =====================
// Utility Functions
// =====================

/**
 * Generate a URL-friendly slug from a tag name
 */
export function generateTagSlug(name: string): string {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
}

/**
 * Validate if a color is a valid hex color code
 */
export function isValidHexColor(color?: string): boolean {
    if (!color) return true;
    return /^#[0-9A-Fa-f]{6}$/.test(color);
}

/**
 * Get default tag colors
 */
export const DEFAULT_TAG_COLORS = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#96CEB4', // Green
    '#FFEAA7', // Yellow
    '#DDA0DD', // Plum
    '#98D8C8', // Mint
    '#F7DC6F', // Light Yellow
    '#BB8FCE', // Light Purple
    '#85C1E9', // Light Blue
] as const;

/**
 * Get a random default color
 */
export function getRandomTagColor(): string {
    return DEFAULT_TAG_COLORS[Math.floor(Math.random() * DEFAULT_TAG_COLORS.length)] ?? DEFAULT_TAG_COLORS[0] ?? '#FF6B6B';
}

/**
 * Check if a tag is available for a specific module
 * All tags are now unified and available for all modules
 */
export function isTagAvailableForModule(tag: TagDefinition, moduleType: string): boolean {
    return true;
}
